import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getBooksByLibrary,
  searchBooksByLibrary,
  updateBookStatus,
  deleteBook,
  getBookCategories,
  addBookCategory,
  updateBookCategory,
  deleteBookCategory,
} from '../../api/api';

import '../BookList.css';
import '../lib-page-header.css';
import { exportToCSV } from '../exportUtils';

const getErrorMessage = (e) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Something went wrong';

const PAGE_SIZE = 10;

const BookList = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const libraryId = useActiveLibraryId();

  /* ── Books state ── */
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [selectedPublisher, setSelectedPublisher] = useState('');
  const [selectedBook, setSelectedBook] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  /* ── Category filter dropdown ── */
  const [catDropOpen, setCatDropOpen] = useState(false);
  const [catDropLoading, setCatDropLoading] = useState(false);
  const catDropRef = useRef(null);

  /* ── Category manager modal ── */
  const [catModal, setCatModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [catSearch, setCatSearch] = useState('');

  /* ── Add / Edit form inside modal ── */
  const [catForm, setCatForm] = useState({ id: null, name: '', description: '' });
  const [catFormMode, setCatFormMode] = useState(''); // 'add' | 'edit'
  const [catSaving, setCatSaving] = useState(false);

  /* ════════════════════════════════
     Books API
  ═══════════════════════════════════ */
  const fetchBooks = async (cat = selectedCategory) => {
    try {
      if (!libraryId) return;
      setLoading(true);
      const data = await getBooksByLibrary(libraryId);
      const all = Array.isArray(data) ? data : [];
      setBooks(cat ? all.filter((b) => String(b.category_id) === String(cat)) : all);
    } catch (err) {
      Swal.fire('Error', getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBooks(); }, [libraryId]);

  /* Close category dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (catDropRef.current && !catDropRef.current.contains(e.target))
        setCatDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = async () => {
    try {
      if (!libraryId) return;
      setLoading(true);
      const raw = search.trim()
        ? await searchBooksByLibrary(libraryId, search.trim())
        : await getBooksByLibrary(libraryId);
      const all = Array.isArray(raw) ? raw : [];
      setBooks(selectedCategory ? all.filter((b) => String(b.category_id) === String(selectedCategory)) : all);
    } catch (err) {
      Swal.fire('Error', getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedAuthor('');
    setSelectedPublisher('');
    setSelectedBook('');
    fetchBooks('');
  };

  const handleClearSearch = () => {
    setSearch('');
    fetchBooks(selectedCategory);
  };

  const handleCategoryFilter = (catId) => {
    setSelectedCategory(catId);
    setCatDropOpen(false);
    fetchBooks(catId);
  };

  const handleStatusChange = async (book) => {
    const newStatus = book.status === 'active' ? 'inactive' : 'active';

    const confirmed = await Swal.fire({
      title: `Mark "${book.title}" as ${newStatus}?`,
      text:
        newStatus === 'inactive'
          ? 'Inactive books will not be available for new issues or reservations.'
          : 'This book will become available for issues and reservations again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, mark ${newStatus}`,
      cancelButtonText: 'Cancel',
    });

    if (!confirmed.isConfirmed) return;

    try {
      await updateBookStatus(book.id, newStatus);
      Swal.fire({ icon: 'success', title: 'Success', text: `Book marked as ${newStatus}` });
      fetchBooks();
    } catch (err) {
      Swal.fire('Error', getErrorMessage(err), 'error');
    }
  };

  const handleDelete = async (book) => {
    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'Book will be deleted only if no copies are linked.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed.isConfirmed) return;
    try {
      await deleteBook(book.id);
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Book deleted successfully' });
      fetchBooks();
    } catch (err) {
      Swal.fire('Error', getErrorMessage(err), 'error');
    }
  };

  const getBadgeClass = (status) => {
    if (status === 'active') return 'book-badge-success';
    if (status === 'inactive') return 'book-badge-secondary';
    return 'book-badge-light';
  };

  /* ════════════════════════════════
     Categories API
  ═══════════════════════════════════ */
  const fetchCategories = async () => {
    try {
      if (!libraryId) return;
      setCatLoading(true);
      setCatDropLoading(true);
      const data = await getBookCategories(libraryId);
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      Swal.fire('Error', getErrorMessage(err), 'error');
    } finally {
      setCatLoading(false);
      setCatDropLoading(false);
    }
  };

  const openCatModal = () => {
    setCatModal(true);
    setCatFormMode('');
    setCatForm({ id: null, name: '', description: '' });
    fetchCategories();
  };

  const closeCatModal = () => {
    setCatModal(false);
    setCatFormMode('');
    setCatSearch('');
  };

  const startAdd = () => {
    setCatForm({ id: null, name: '', description: '' });
    setCatFormMode('add');
  };

  const startEdit = (cat) => {
    setCatForm({ id: cat.id, name: cat.name, description: cat.description || '' });
    setCatFormMode('edit');
  };

  const cancelForm = () => {
    setCatFormMode('');
    setCatForm({ id: null, name: '', description: '' });
  };

 const handleCatSave = async () => {
  if (!catForm.name.trim()) {
    Swal.fire('Validation', 'Category name is required', 'warning');
    return;
  }

  if (!libraryId) {
    Swal.fire('Validation', 'Library ID is missing. Please login again.', 'warning');
    return;
  }

  try {
    setCatSaving(true);

    const payload = {
      library_id: libraryId,
      name: catForm.name.trim(),
      description: catForm.description?.trim() || ''
    };

    if (catFormMode === 'add') {
      await addBookCategory(payload);

      Swal.fire({
        icon: 'success',
        title: 'Added',
        text: 'Category added successfully',
        timer: 1500,
        showConfirmButton: false
      });
    } else {
      await updateBookCategory(catForm.id, payload);

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Category updated successfully',
        timer: 1500,
        showConfirmButton: false
      });
    }

    cancelForm();
    fetchCategories();
    fetchBooks();

  } catch (err) {
    Swal.fire('Error', getErrorMessage(err), 'error');
  } finally {
    setCatSaving(false);
  }
};

  const handleCatDelete = async (cat) => {
    const confirmed = await Swal.fire({
      title: `Delete "${cat.name}"?`,
      text: 'Books in this category may be affected.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed.isConfirmed) return;
    try {
      await deleteBookCategory(cat.id);
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1500, showConfirmButton: false });
      fetchCategories();
      if (String(selectedCategory) === String(cat.id)) handleCategoryFilter('');
    } catch (err) {
      Swal.fire('Error', getErrorMessage(err), 'error');
    }
  };

  const filteredCats = categories.filter((c) =>
    c.name.toLowerCase().includes(catSearch.toLowerCase())
  );

  const activeCatName = selectedCategory
    ? categories.find((c) => String(c.id) === String(selectedCategory))?.name
    : null;

  const authorOptions = useMemo(() => {
    const names = books.map((b) => b.author_name).filter(Boolean);
    return Array.from(new Set(names)).sort();
  }, [books]);

  const publisherOptions = useMemo(() => {
    const names = books.map((b) => b.publisher_name).filter(Boolean);
    return Array.from(new Set(names)).sort();
  }, [books]);

  const bookOptions = useMemo(() => {
    const names = books.map((b) => b.title).filter(Boolean);
    return Array.from(new Set(names)).sort();
  }, [books]);

  const filteredBooks = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return books.filter((b) => {
      if (selectedBook && b.title !== selectedBook) return false;
      if (selectedAuthor && b.author_name !== selectedAuthor) return false;
      if (selectedPublisher && b.publisher_name !== selectedPublisher) return false;
      if (kw) {
        const match =
          String(b.title || '').toLowerCase().includes(kw) ||
          String(b.author_name || '').toLowerCase().includes(kw) ||
          String(b.publisher_name || '').toLowerCase().includes(kw) ||
          String(b.isbn || '').toLowerCase().includes(kw) ||
          String(b.category_name || '').toLowerCase().includes(kw);
        if (!match) return false;
      }
      return true;
    });
  }, [books, search, selectedBook, selectedAuthor, selectedPublisher]);

  const sortedBooks = useMemo(() => {
    if (!sortField) return filteredBooks;

    const list = [...filteredBooks];

    list.sort((a, b) => {
      let valA;
      let valB;

      if (sortField === 'status') {
        valA = String(a.status || '').toLowerCase();
        valB = String(b.status || '').toLowerCase();
      } else {
        valA = Number(a[sortField] || 0);
        valB = Number(b[sortField] || 0);
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [filteredBooks, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedBooks.length / PAGE_SIZE));

  const paginatedBooks = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedBooks.slice(start, start + PAGE_SIZE);
  }, [sortedBooks, page]);

  useEffect(() => {
    setPage(1);
  }, [books, selectedBook, selectedAuthor, selectedPublisher]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return <i className="ti ti-arrows-sort" style={{ opacity: 0.4, marginLeft: 4 }} />;
    return sortDir === 'asc' ? (
      <i className="ti ti-sort-ascending" style={{ marginLeft: 4 }} />
    ) : (
      <i className="ti ti-sort-descending" style={{ marginLeft: 4 }} />
    );
  };

  const exportColumns = [
    { header: 'Book', key: 'title' },
    { header: 'Category', key: 'category_name' },
    { header: 'Author', key: 'author_name' },
    { header: 'Publisher', key: 'publisher_name' },
    { header: 'ISBN', key: 'isbn' },
    { header: 'Total', key: 'total_copies' },
    { header: 'Available', key: 'available_copies' },
    { header: 'Issued', key: 'issued_copies' },
    { header: 'Reserved', key: 'reserved_copies' },
    { header: 'Status', key: 'status' }
  ];

  const handleExportCSV = () => exportToCSV(exportColumns, sortedBooks, 'books_list');

  /* ════════════════════════════════
     Render
  ═══════════════════════════════════ */
  return (
    <div className="book-list-page">

      {/* ── Page Header ── */}
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-book" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Books</h3>
            <div className="lib-page-header__subtitle">Manage book master, copies and availability</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button type="button" className="book-cat-manage-btn" onClick={openCatModal}>
            <i className="ti ti-tags" /> Manage Categories
          </button>
          <button type="button" className="book-cat-manage-btn" onClick={() => navigate(`${sectionBase}/book-import`)}>
            <i className="ti ti-file-import" /> Import Books
          </button>
          <button type="button" className="book-add-btn" onClick={() => navigate(`${sectionBase}/books/add`)}>
            + Add Book
          </button>
          <button type="button" className="book-cat-manage-btn" onClick={handleExportCSV}>
            <i className="ti ti-file-spreadsheet" /> CSV
          </button>
        </div>
      </div>

      {/* ── Filter Card ── */}
      <div className="book-filter-card">
        <div className="book-filter-row">
          <div className="search__input">
            <input
              type="text"
              className="book-search-input"
              placeholder="Search by title, author, publisher, ISBN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setSearch('')}
              style={{ width: '100%' }}
            />
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                title="Clear search"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 16, lineHeight: 1
                }}
              >
                <i className="ti ti-x" />
              </button>
            )}
          </div>

          {/* Book filter */}
          <SearchableSelect
            name="book_filter"
            className="book-filter-dropdown-wrap"
            value={selectedBook}
            options={bookOptions}
            placeholder="All Books"
            onChange={(e) => setSelectedBook(e.target.value)}
          />

          {/* Author filter */}
          <SearchableSelect
            name="author_filter"
            className="book-filter-dropdown-wrap"
            value={selectedAuthor}
            options={authorOptions}
            placeholder="All Authors"
            onChange={(e) => setSelectedAuthor(e.target.value)}
          />

          {/* Publisher filter */}
          <SearchableSelect
            name="publisher_filter"
            className="book-filter-dropdown-wrap"
            value={selectedPublisher}
            options={publisherOptions}
            placeholder="All Publishers"
            onChange={(e) => setSelectedPublisher(e.target.value)}
          />

          {/* Category filter dropdown */}
          <div className="book-cat-dropdown-wrap" ref={catDropRef}>
            <button
              type="button"
              className={`book-cat-filter-btn ${selectedCategory ? 'active' : ''}`}
              onClick={() => { setCatDropOpen((p) => !p); fetchCategories(); }}
            >
              <i className="ti ti-filter" />
              {activeCatName ? activeCatName : 'Category'}
              <i className={`ti ti-chevron-${catDropOpen ? 'up' : 'down'}`} />
            </button>

            {catDropOpen && (
              <div className="book-cat-dropdown">
                <div className="book-cat-drop-search">
                  <i className="ti ti-search" />
                  <input
                    type="text"
                    placeholder="Search category..."
                    value={catSearch}
                    onChange={(e) => setCatSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="book-cat-drop-list">
                  {catDropLoading ? (
                    <div className="book-cat-drop-empty">Loading categories...</div>
                  ) : (
                    <>
                      <div
                        className={`book-cat-drop-item ${!selectedCategory ? 'selected' : ''}`}
                        onClick={() => handleCategoryFilter('')}
                      >
                        All Categories
                      </div>
                      {filteredCats.map((cat) => (
                        <div
                          key={cat.id}
                          className={`book-cat-drop-item ${String(selectedCategory) === String(cat.id) ? 'selected' : ''}`}
                          onClick={() => handleCategoryFilter(cat.id)}
                        >
                          {cat.name}
                          <span className="book-cat-drop-count">{cat.total_books ?? ''}</span>
                        </div>
                      ))}
                      {filteredCats.length === 0 && (
                        <div className="book-cat-drop-empty">No categories found</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <button type="button" className="book-reset-btn" onClick={handleReset}>Reset</button>
        </div>

        {/* Active filter chip */}
        {selectedCategory && activeCatName && (
          <div className="book-filter-chips">
            <span className="book-filter-chip">
              Category: <strong>{activeCatName}</strong>
              <button onClick={() => handleCategoryFilter('')}><i className="ti ti-x" /></button>
            </span>
          </div>
        )}
      </div>

      {/* ── Table Card ── */}
      <div className="book-table-card">
        {showLoader ? (
          <Loader />
        ) : (
          <div className="book-table-wrapper">
            <table className="book-table">
              <thead>
                <tr>
                  <th>#</th><th>Book</th><th>Category</th><th>Author</th>
                  <th>Publisher</th><th>ISBN</th>
                  <th role="button" onClick={() => handleSort('total_copies')} style={{ cursor: 'pointer' }}>
                    Total {sortIcon('total_copies')}
                  </th>
                  <th role="button" onClick={() => handleSort('available_copies')} style={{ cursor: 'pointer' }}>
                    Available {sortIcon('available_copies')}
                  </th>
                  <th role="button" onClick={() => handleSort('issued_copies')} style={{ cursor: 'pointer' }}>
                    Issued {sortIcon('issued_copies')}
                  </th>
                  <th role="button" onClick={() => handleSort('reserved_copies')} style={{ cursor: 'pointer' }}>
                    Reserved {sortIcon('reserved_copies')}
                  </th>
                  <th role="button" onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status {sortIcon('status')}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBooks.length > 0 ? paginatedBooks.map((book, i) => (
                  <tr key={book.id}>
                    <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td>
                      <div className="book-title">{book.title}</div>
                      {book.subtitle && <div className="book-subtitle">{book.subtitle}</div>}
                    </td>
                    <td>{book.category_name || '-'}</td>
                    <td>{book.author_name || '-'}</td>
                    <td>{book.publisher_name || '-'}</td>
                    <td>{book.isbn || '-'}</td>
                    <td>{book.total_copies || 0}</td>
                    <td><span className="book-count book-count-green">{book.available_copies || 0}</span></td>
                    <td><span className="book-count book-count-blue">{book.issued_copies || 0}</span></td>
                    <td><span className="book-count book-count-yellow">{book.reserved_copies || 0}</span></td>
                    <td><span className={`book-status ${getBadgeClass(book.status)}`}>{book.status || '-'}</span></td>
                    <td>
                      <div className="book-action-buttons">
                        <button className="book-action-btn view" onClick={() => navigate(`${sectionBase}/books/${book.id}`)}>View</button>
                        <button className="book-action-btn edit" onClick={() => navigate(`${sectionBase}/books/${book.id}/edit`)}>Edit</button>
                        <button className="book-action-btn copies" onClick={() => navigate(`${sectionBase}/book-copies?book_id=${book.id}`)}>Copies</button>
                        <button className="book-action-btn status" onClick={() => handleStatusChange(book)}>
                          {book.status === 'active' ? 'Inactive' : 'Active'}
                        </button>
                        <button className="book-action-btn delete" onClick={() => handleDelete(book)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="12" className="book-empty">No books found</td></tr>
                )}
              </tbody>
            </table>

            {sortedBooks.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  Page {page} of {totalPages} ({sortedBooks.length} books)
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="book-reset-btn"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="book-reset-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ════════════════════════════════
          Category Manager Modal
      ═══════════════════════════════════ */}
      {catModal && (
        <div className="cat-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeCatModal()}>
          <div className="cat-modal">

            {/* Modal header */}
            <div className="cat-modal-header">
              <div>
                <h4>Manage Categories</h4>
                <p>Add, edit or delete book categories</p>
              </div>
              <button className="cat-modal-close" onClick={closeCatModal}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Add / Edit form */}
            {catFormMode ? (
              <div className="cat-form">
                <h5>{catFormMode === 'add' ? 'Add New Category' : 'Edit Category'}</h5>
                <div className="cat-form-row">
                  <div className="cat-form-group">
                    <label>Category Name <span>*</span></label>
                    <input
                      type="text"
                      placeholder="Enter category name"
                      value={catForm.name}
                      onChange={(e) => setCatForm((p) => ({ ...p, name: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleCatSave()}
                      autoFocus
                    />
                  </div>
                  <div className="cat-form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      placeholder="Optional description"
                      value={catForm.description}
                      onChange={(e) => setCatForm((p) => ({ ...p, description: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="cat-form-actions">
                  <button className="cat-btn-save" onClick={handleCatSave} disabled={catSaving}>
                    {catSaving ? 'Saving...' : catFormMode === 'add' ? 'Add Category' : 'Update Category'}
                  </button>
                  <button className="cat-btn-cancel" onClick={cancelForm}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="cat-modal-toolbar">
                <div className="cat-modal-search">
                  <i className="ti ti-search" />
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={catSearch}
                    onChange={(e) => setCatSearch(e.target.value)}
                  />
                </div>
                <button className="cat-btn-add" onClick={startAdd}>
                  <i className="ti ti-plus" /> Add Category
                </button>
              </div>
            )}

            {/* Category list */}
            <div className="cat-modal-body">
              {catLoading ? (
                <div className="cat-modal-loading">Loading categories...</div>
              ) : filteredCats.length > 0 ? (
                <table className="cat-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category Name</th>
                      <th>Description</th>
                      <th>Books</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCats.map((cat, i) => (
                      <tr key={cat.id}>
                        <td>{i + 1}</td>
                        <td><strong>{cat.name}</strong></td>
                        <td>{cat.description || '-'}</td>
                        <td><span className="book-count book-count-blue">{cat.total_books ?? 0}</span></td>
                        <td>
                          <span className={`book-status ${cat.status === 'active' ? 'book-badge-success' : 'book-badge-secondary'}`}>
                            {cat.status || 'active'}
                          </span>
                        </td>
                        <td>
                          <div className="book-action-buttons">
                            <button className="book-action-btn edit" onClick={() => startEdit(cat)}>Edit</button>
                            <button className="book-action-btn delete" onClick={() => handleCatDelete(cat)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="cat-modal-empty">
                  <i className="ti ti-tags" />
                  <p>{catSearch ? 'No categories match your search' : 'No categories yet'}</p>
                  {!catSearch && <button className="cat-btn-add" onClick={startAdd}><i className="ti ti-plus" /> Add First Category</button>}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="cat-modal-footer">
              <span>{categories.length} categories total</span>
              <button className="cat-btn-cancel" onClick={closeCatModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookList;
